/* MelomyPet product gallery viewer. Alp, 2026-10-19: «görsele tıklayınca büyümüyor, yazılar okunmuyor, sertifika hiç
   görünmüyor». Pressing the picture (or the magnifier) opens a full-screen viewer that shows the whole picture as large
   as the screen allows, loaded at the file's own resolution. Pressing the picture again zooms in where it was pressed;
   drag (mouse) or scroll and swipe (touch) to move around, and press once more to go back. Arrows, a sideways swipe and
   ← → move between the pictures the gallery shows now (the perfume shows one scent's); Esc closes.
   Markup: sections/studio-product.liquid ([data-product-zoom]); styles: assets/product-zoom.css. */
(() => {
  const FULL = 2400;          // Shopify serves at most the file's own width
  const ZOOM_MIN = 2;         // zoomed width: the file's width, but at least 2× and at most 4× the fitted width
  const ZOOM_MAX = 4;
  const TAP = 8;              // px a press may move and still count as a press
  const SWIPE = 50;           // px sideways that move to the next picture

  function fullSize(src) {
    try {
      const url = new URL(src, location.href);
      url.searchParams.set('width', FULL);
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      return url.href;
    } catch (error) {
      return src;
    }
  }

  function setup(root) {
    const dialog = root.querySelector('[data-product-zoom]');
    const stage = root.querySelector('.product-gallery-stage');
    if (!dialog || !stage || typeof dialog.showModal !== 'function') return;
    const opener = root.querySelector('[data-zoom-open]');
    const viewport = dialog.querySelector('[data-zoom-viewport]');
    const image = dialog.querySelector('[data-zoom-image]');
    const count = dialog.querySelector('[data-zoom-count]');
    const prev = dialog.querySelector('[data-zoom-prev]');
    const next = dialog.querySelector('[data-zoom-next]');
    let items = [];
    let index = 0;
    let zoomed = false;
    let fitted = null;          // the picture's fitted size when it was zoomed
    let sharpWidth = 0;         // the full-resolution file's width, once it has loaded
    let press = null;
    let returnFocus = null;

    // the pictures the gallery shows now: image frames whose thumbnail is not hidden
    function visibleItems() {
      return [...root.querySelectorAll('[data-product-media]')].filter((frame) => {
        if (!frame.querySelector('img')) return false;
        const thumb = root.querySelector(`[data-media-target="${frame.dataset.productMedia}"]`);
        return !thumb || !thumb.hidden;
      });
    }

    function source(frame) {
      const picture = frame && frame.querySelector('img');
      return picture ? (picture.currentSrc || picture.src) : '';
    }

    function show(target) {
      unzoom();
      index = (target + items.length) % items.length;
      const picture = items[index].querySelector('img');
      const shown = source(items[index]);
      const full = fullSize(shown);
      image.alt = picture.alt || '';
      if (image.dataset.full !== full) {
        image.dataset.full = full;
        sharpWidth = 0;
        image.src = shown;                                   // the size the page already loaded, at once
        const sharp = new Image();
        sharp.onload = () => {
          if (image.dataset.full !== full) return;
          sharpWidth = sharp.naturalWidth;
          image.src = full;
          if (zoomed) rezoom();                              // zoomed before the sharp file came: zoom as far as it allows
        };
        sharp.src = full;
      }
      count.textContent = items.length > 1 ? `${index + 1} / ${items.length}` : '';
      prev.hidden = next.hidden = items.length < 2;
      if (items.length > 1) [index + 1, index - 1].forEach((n) => { new Image().src = fullSize(source(items[(n + items.length) % items.length])); });
    }

    function open(frame) {
      items = visibleItems();
      if (!items.length) return;
      const start = items.indexOf(frame || items.find((item) => !item.hidden));
      returnFocus = document.activeElement;
      document.documentElement.classList.add('product-zoom-lock');
      dialog.showModal();
      show(Math.max(0, start));
    }

    function zoomWidth(fitWidth) {
      const natural = Math.max(sharpWidth, image.naturalWidth || 0) || fitWidth;
      return Math.round(Math.min(Math.max(natural, fitWidth * ZOOM_MIN), fitWidth * ZOOM_MAX));
    }

    function zoomAt(x, y) {
      const fit = image.getBoundingClientRect();
      if (!fit.width || !fit.height) return;
      const across = Math.min(1, Math.max(0, (x - fit.left) / fit.width));
      const down = Math.min(1, Math.max(0, (y - fit.top) / fit.height));
      fitted = { width: fit.width, height: fit.height };
      const width = zoomWidth(fit.width);
      const height = Math.round(width * fit.height / fit.width);
      zoomed = true;
      dialog.classList.add('is-zoomed');
      image.style.width = `${width}px`;
      image.style.height = `${height}px`;
      const box = viewport.getBoundingClientRect();
      viewport.scrollLeft = across * width - (x - box.left);   // keep the pressed point under the finger or pointer
      viewport.scrollTop = down * height - (y - box.top);
    }

    function rezoom() {
      const before = parseFloat(image.style.width);
      const width = zoomWidth(fitted.width);
      if (!before || width === before) return;
      const box = viewport.getBoundingClientRect();
      const across = (viewport.scrollLeft + box.width / 2) / before;      // keep the middle of the view where it is
      const down = (viewport.scrollTop + box.height / 2) / parseFloat(image.style.height);
      const height = Math.round(width * fitted.height / fitted.width);
      image.style.width = `${width}px`;
      image.style.height = `${height}px`;
      viewport.scrollLeft = across * width - box.width / 2;
      viewport.scrollTop = down * height - box.height / 2;
    }

    function unzoom() {
      if (!zoomed) return;
      zoomed = false;
      dialog.classList.remove('is-zoomed', 'is-dragging');
      image.style.width = '';
      image.style.height = '';
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }

    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      press = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop, moved: false, mouse: event.pointerType === 'mouse', onImage: event.target === image };
      if (zoomed && press.mouse) {
        viewport.setPointerCapture(event.pointerId);
        dialog.classList.add('is-dragging');
      }
    });
    viewport.addEventListener('pointermove', (event) => {
      if (!press) return;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (Math.abs(dx) > TAP || Math.abs(dy) > TAP) press.moved = true;
      if (zoomed && press.mouse) {
        viewport.scrollLeft = press.left - dx;
        viewport.scrollTop = press.top - dy;
      }
    });
    viewport.addEventListener('pointerup', (event) => {
      if (!press) return;
      const done = press;
      press = null;
      dialog.classList.remove('is-dragging');
      const dx = event.clientX - done.x;
      const dy = event.clientY - done.y;
      if (!zoomed && !done.mouse && items.length > 1 && Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy) * 1.5) {
        show(index + (dx < 0 ? 1 : -1));
        return;
      }
      if (done.moved) return;
      if (zoomed) unzoom();
      else if (done.onImage) zoomAt(event.clientX, event.clientY);
      else dialog.close();
    });
    viewport.addEventListener('pointercancel', () => {   // the browser took the gesture over (scrolling, pinching)
      press = null;
      dialog.classList.remove('is-dragging');
    });

    dialog.addEventListener('keydown', (event) => {
      if (items.length < 2) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
    });
    dialog.addEventListener('close', () => {
      unzoom();
      document.documentElement.classList.remove('product-zoom-lock');
      // the gallery stays on the picture seen last, without changing the chosen variant
      const id = items[index] && items[index].dataset.productMedia;
      if (id) {
        root.querySelectorAll('[data-product-media]').forEach((frame) => { frame.hidden = frame.dataset.productMedia !== id; });
        root.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === id)));
      }
      const back = returnFocus && returnFocus !== document.body && document.contains(returnFocus) ? returnFocus : opener;
      if (back) back.focus({ preventScroll: true });
    });
    dialog.querySelector('[data-zoom-close]').addEventListener('click', () => dialog.close());
    prev.addEventListener('click', () => show(index - 1));
    next.addEventListener('click', () => show(index + 1));

    stage.addEventListener('click', (event) => {
      const frame = event.target.closest('[data-product-media]');
      if (frame && event.target.tagName === 'IMG') open(frame);
    });
    if (opener) {
      opener.hidden = false;
      opener.addEventListener('click', () => open());
    }
    addEventListener('resize', unzoom);
  }

  const boot = () => document.querySelectorAll('[data-product-root]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
